import React, { useEffect, useState } from "react";
import { CoreTracking } from "../../../../models/CoreTracking";
import { useDispatch, useSelector } from "react-redux";
import { ClearRestoreGoogleUserTracking, RestoreGoogleUserTracking } from "../../../../store/TrackingOperationAction";
import ModalComponent from "../../../misc/ModalComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusCode from "../../../../util/StatusCode";
import Loading from "../../../misc/Loading";
import { RequestState } from "../../../../models/RequestState";
import { CoreGoogleUserTrackingConflict } from "../../../../models/CoreTrackingOperation";
import { ExportGoogleUserTrackingMetadata } from "../../../../models/TrackingMetadata";
import GoogleUserTrackingOperationsValidationResult from "./GoogleUserTrackingOperationsValidationResult";
import CoreApiError from "../../../misc/CoreApiError";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface RestoreGoogleUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const RestoreGoogleUserTrackingWizard: React.FC<RestoreGoogleUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const metadata = JSON.parse(props.tracking.metadata) as ExportGoogleUserTrackingMetadata;
    const [wizardStep, setWizardStep] = useState<'confirm' | 'done'>('confirm');
    const restoreGoogleUserTracking = useSelector<RootState, RequestState<CoreGoogleUserTrackingConflict[]>>(state => state.trackingOperation.restoreGoogleUserTracking)
    const { t } = useTranslation(['schoolAdministration', 'common']);

    useEffect(() => {
        RestoreGoogleUserTracking(props.tracking.trackingId, true)(dispatch);
        return () => ClearRestoreGoogleUserTracking()(dispatch);
    }, [props, dispatch]);

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        RestoreGoogleUserTracking(props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        setWizardStep('confirm');
        props.close(success);
    }

    switch (wizardStep) {
        case 'confirm': {
            if (restoreGoogleUserTracking.code === StatusCode.COMPLETE) {
                if (restoreGoogleUserTracking.data.length === 0)
                {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                        update={confirmInput}
                        confirm={t('action.confirm', {ns: 'common'})}
                        cancel={t('action.close', {ns: 'common'})}>
                        <p><FontAwesomeIcon title={t('status.OK', {ns: 'common'})} color="green" icon={['fas', 'check-circle']} /> {t('trackingOperations.general.noErrorsConfirm', {ns: 'schoolAdministration'})}</p>
                        <p><FontAwesomeIcon title={t('status.warning', {ns: 'common'})} color="#fc1" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.google.edit.verifyNotTaken', {ns: 'schoolAdministration', PrimaryEmail: metadata.PrimaryEmail, SourceSystemId: props.tracking.sourceSystemId})}</p>
                    </ModalComponent>
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <p><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} /> {t('trackingOperations.general.conflictsDiscovered', {ns: 'schoolAdministration'})}</p>
                    <GoogleUserTrackingOperationsValidationResult googleId={props.tracking.sourceSystemId} primaryEmail={metadata.PrimaryEmail} conflicts={restoreGoogleUserTracking.data} />
                </ModalComponent>;
            } else if (restoreGoogleUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={restoreGoogleUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (restoreGoogleUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.restoreCompleted', {ns: 'schoolAdministration'})}</ModalComponent>
            } else if (restoreGoogleUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}><CoreApiError  requestState={restoreGoogleUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.google.restore.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

export default RestoreGoogleUserTrackingWizard;
